import type { CSSProperties } from 'react';
import { Users } from 'lucide-react';
import type { LobbyGame, LobbyImageType } from '../api/getLobby';
import {
  formatEntryCount,
  formatPicksLockEt,
  formatWinPrize,
  gameEntryCount,
  gameTypeLabel,
  heroHeadline,
  heroSubtitle,
  sportLabel,
} from '../utils/lobbyGameDisplay';
import { isCustomLobbyGame } from '../utils/lobbyPlayableStatus';
import type { LobbyCardMetadataOptions } from '../utils/lobbySectionMetadata';
import { hasVisibleLobbyCardMetadata } from '../utils/lobbySectionMetadata';

export type { LobbyCardMetadataOptions };

export type LobbyCardDisplaySlot = LobbyImageType;

interface LobbyGameCardProps {
  game: LobbyGame;
  displaySlot: LobbyCardDisplaySlot;
  imageUrl: string | null;
  href: string | null;
  className?: string;
  style?: CSSProperties;
  /** When set, metadata uses medium-style layout with per-field visibility toggles. */
  metadataOptions?: LobbyCardMetadataOptions;
  /** When provided, intercepts the anchor click and calls this instead of navigating */
  onClick?: () => void;
}

function OverlayBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium leading-none text-white backdrop-blur-sm">
      {children}
    </span>
  );
}

/**
 * Bottom-right "Play Now" button.
 *
 * A `span`, not a button element: the whole card is already an anchor to the game, so a nested
 * interactive element would be invalid HTML and a second tab stop to the same destination. It
 * is a visual affordance — a click on it lands on the card anchor like any other, which is
 * what makes both it and the card navigate with no extra handler.
 *
 * Padding is kept close to the label per design: a compact rounded rectangle that reads as a
 * button, not a pill and not a wide bar.
 *
 * TWO STATES, the visited/unvisited convention applied to contests:
 *  - NOT ENTERED — DK green. An invitation to start something.
 *  - ENTERED — a semi-transparent grey. The contest is still one tap away (the card navigates
 *    identically); the colour just stops it competing for attention against the contests the
 *    viewer has not played yet, and tells them where to jump back in.
 *
 * Colour is never the ONLY signal: the entered state also carries its own `title` and
 * `aria-label`, so it survives a screen reader and a colour-vision deficiency. The visible
 * label stays "Play Now" in both states, per design.
 */
function PlayNowCta({ entered }: { entered: boolean }) {
  return (
    <span
      data-testid="lobby-card-play-now"
      data-entered={entered ? 'true' : 'false'}
      title={entered ? "You've entered this contest — jump back in" : undefined}
      aria-label={entered ? "Play Now — you've already entered this contest" : undefined}
      className={[
        'absolute bottom-3 right-3 z-10 inline-flex items-center rounded-[8px] px-3 py-2 text-xs font-bold leading-none shadow-[0_2px_8px_rgba(0,0,0,0.45)] transition',
        entered
          ? 'border border-white/25 bg-white/25 text-white backdrop-blur-sm group-hover:bg-white/35'
          : 'bg-[#5AED38] text-black group-hover:bg-[#6FF04E]',
      ].join(' ')}
    >
      Play Now
    </span>
  );
}

function CardImage({ imageUrl, alt }: { imageUrl: string | null; alt: string }) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={alt}
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
      />
    );
  }
  return <div className="absolute inset-0 bg-gradient-to-r from-[#24313f] to-[#0f1722]" />;
}

function HeroOverlay({ game }: { game: LobbyGame }) {
  const picksLock = formatPicksLockEt(game.start_utc);
  const prize = formatWinPrize(game);
  const description = heroSubtitle(game);
  const typeLabel = gameTypeLabel(game.game_type);
  const sport = sportLabel(game.sport);

  return (
    <>
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/15" />
      <div className="absolute inset-x-0 bottom-0 p-4">
        <div className="flex items-center gap-2">
          {sport ? (
            <span className="rounded bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white/90">
              {sport}
            </span>
          ) : null}
          {typeLabel ? (
            <span className="text-xs font-semibold uppercase tracking-wider text-green-300">
              {typeLabel}
            </span>
          ) : null}
        </div>
        <h2 className="mt-1 text-3xl font-black uppercase leading-none tracking-tight text-white md:text-4xl">
          {heroHeadline(game)}
        </h2>
        {picksLock ? (
          <p className="mt-2">
            <OverlayBadge>{picksLock}</OverlayBadge>
          </p>
        ) : null}
        {prize ? <p className="mt-2 text-lg font-bold text-white/95">{prize}</p> : null}
        {description ? <p className="mt-1 text-sm text-white/85">{description}</p> : null}
        <div className="mt-4 flex h-11 items-center justify-center rounded-xl border border-white/25 bg-black/35 text-base font-semibold text-white">
          Play for Free
        </div>
      </div>
    </>
  );
}

function LargeOverlay({ game }: { game: LobbyGame }) {
  const picksLock = formatPicksLockEt(game.start_utc);
  const prize = formatWinPrize(game);
  const entries = formatEntryCount(gameEntryCount(game));
  const sport = sportLabel(game.sport);
  const title = game.title_en?.trim() || 'Untitled';

  return (
    <>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-3">
        <div className="flex items-center gap-1.5">
          {sport ? (
            <span className="text-[10px] font-bold uppercase tracking-wide text-white/80">
              {sport}
            </span>
          ) : null}
          <p className="truncate text-sm font-semibold text-white">{title}</p>
        </div>
        {prize ? (
          <p className="mt-0.5 truncate text-xs font-medium text-white/90">{prize}</p>
        ) : null}
        {(entries || picksLock) && (
          // pr-24 keeps the badges clear of the Play Now pill in the opposite corner.
          <div className="mt-2 flex flex-wrap gap-1.5 pr-24">
            {entries ? (
              <OverlayBadge>
                <Users className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
                {entries}
              </OverlayBadge>
            ) : null}
            {picksLock ? <OverlayBadge>{picksLock}</OverlayBadge> : null}
          </div>
        )}
      </div>
    </>
  );
}

function ConfigurableMetadataOverlay({
  game,
  options,
}: {
  game: LobbyGame;
  options: LobbyCardMetadataOptions;
}) {
  const showGradient = options.gradient_overlay;
  const showMetadata = hasVisibleLobbyCardMetadata(options);
  const picksLock = options.start_time ? formatPicksLockEt(game.start_utc) : null;
  const prize = options.prize_pool ? formatWinPrize(game) : null;
  const entries = options.entries ? formatEntryCount(gameEntryCount(game)) : null;
  const sport = options.sport ? sportLabel(game.sport) : null;
  const title = options.contest_name ? game.title_en?.trim() || 'Untitled' : null;

  if (!showGradient && !showMetadata) {
    return null;
  }

  return (
    <>
      {showGradient ? (
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
      ) : null}
      {showMetadata ? (
        <div className="absolute inset-0 flex flex-col justify-between p-3">
          <div className="min-w-0 pr-16">
            {sport ? (
              <span className="text-[10px] font-bold uppercase tracking-wide text-white/80">
                {sport}
              </span>
            ) : null}
            {title ? (
              <p className="mt-0.5 line-clamp-2 text-sm font-semibold leading-tight text-white">
                {title}
              </p>
            ) : null}
            {prize ? (
              <p className="mt-1 line-clamp-2 text-xs font-medium text-white/90">{prize}</p>
            ) : null}
          </div>
          {(entries || picksLock) && (
            // pr-24 keeps the badges clear of the Play Now pill in the opposite corner.
            <div className="flex flex-col items-start gap-1.5 pr-24">
              {entries ? (
                <OverlayBadge>
                  <Users className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
                  {entries}
                </OverlayBadge>
              ) : null}
              {picksLock ? <OverlayBadge>{picksLock}</OverlayBadge> : null}
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}

function MediumOverlay({ game }: { game: LobbyGame }) {
  const picksLock = formatPicksLockEt(game.start_utc);
  const prize = formatWinPrize(game);
  const entries = formatEntryCount(gameEntryCount(game));
  const sport = sportLabel(game.sport);
  const title = game.title_en?.trim() || 'Untitled';

  return (
    <>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-between p-3">
        <div className="min-w-0 pr-16">
          <div className="flex items-center gap-1.5">
            {sport ? (
              <span className="text-[10px] font-bold uppercase tracking-wide text-white/80">
                {sport}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 line-clamp-2 text-sm font-semibold leading-tight text-white">
            {title}
          </p>
          {prize ? (
            <p className="mt-1 line-clamp-2 text-xs font-medium text-white/90">{prize}</p>
          ) : null}
        </div>
        {(entries || picksLock) && (
          // pr-24 keeps the badges clear of the Play Now pill in the opposite corner.
          <div className="flex flex-col items-start gap-1.5 pr-24">
            {entries ? (
              <OverlayBadge>
                <Users className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
                {entries}
              </OverlayBadge>
            ) : null}
            {picksLock ? <OverlayBadge>{picksLock}</OverlayBadge> : null}
          </div>
        )}
      </div>
    </>
  );
}

export default function LobbyGameCard({
  game,
  displaySlot,
  imageUrl,
  href,
  className = '',
  style,
  metadataOptions,
  onClick,
}: LobbyGameCardProps) {
  const showOverlay = displaySlot !== 'small' && displaySlot !== 'list_item';
  const useConfigurableMetadata = Boolean(metadataOptions);
  const alt = game.title_en || 'Game';

  // The hero already carries a full-width "Play for Free" bar, so it does not take the corner
  // pill as well. The 96px small/list_item tiles have no overlay at all and the pill would
  // cover most of the artwork, so they are out via `showOverlay`.
  const showsHeroCta = showOverlay && !useConfigurableMetadata && displaySlot === 'arcade_header';
  // A card with no destination is rendered inert (`pointer-events-none` below); offering it a
  // Play Now would promise something the card cannot do.
  const showPlayNow = showOverlay && !showsHeroCta && Boolean(href || onClick);

  const handleClick = onClick
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        onClick();
      }
    : undefined;

  const isExternal = isCustomLobbyGame(game);
  // Custom lobby games ALWAYS read as not entered: they point at someone else's game, we hold
  // no entry record for them, and the spec is that their CTA stays green whatever the viewer
  // has done. `has_entry` is optional on the payload, so an older backend (or an anonymous
  // viewer) simply lands on the green state.
  const hasEntered = !isExternal && game.has_entry === true;

  return (
    <a
      href={href || undefined}
      target={isExternal ? '_top' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      aria-disabled={!href}
      onClick={handleClick}
      className={[
        'group relative block overflow-hidden',
        href || onClick ? '' : 'pointer-events-none opacity-70',
        className,
      ].join(' ')}
      style={style}
    >
      <CardImage imageUrl={imageUrl} alt={alt} />
      {showOverlay && useConfigurableMetadata && metadataOptions ? (
        <ConfigurableMetadataOverlay game={game} options={metadataOptions} />
      ) : null}
      {showOverlay && !useConfigurableMetadata && displaySlot === 'arcade_header' ? (
        <HeroOverlay game={game} />
      ) : null}
      {showOverlay && !useConfigurableMetadata && displaySlot === 'large' ? (
        <LargeOverlay game={game} />
      ) : null}
      {showOverlay && !useConfigurableMetadata && displaySlot === 'medium' ? (
        <MediumOverlay game={game} />
      ) : null}
      {showPlayNow ? <PlayNowCta entered={hasEntered} /> : null}
    </a>
  );
}
